import { defineStore } from "pinia";
import { ref } from "vue";
import trainingService from "@/services/hrm/trainingService";
import { toast } from "@/utils/toast";
import { MODULE_KEYS } from "@/config/modules-registry";
import { guardPlanModule } from "@/utils/planAccess";

export const useTrainingStore = defineStore("training", () => {
  const items = ref([]);
  const pagination = ref({ total: 0, per_page: 25, current_page: 1 });
  const loading = ref(false);
  const error = ref(null);

  const filters = ref({
    search: "",
    status: "",
    date_from: "",          // ⬅️ add
    date_to: "",            // ⬅️ add
    per_page: 25,
    page: 1,
  });

  async function fetchAll(extra = {}) {
    if (!(await guardPlanModule(MODULE_KEYS.HRM_TRAINING))) {
      items.value = [];
      pagination.value = { total: 0, per_page: 25, current_page: 1 };
      return;
    }

    loading.value = true;
    error.value = null;
    try {
      const params = { ...filters.value, ...extra };
      const res = await trainingService.getAll(params);
      const data = res.data;

      // Laravel paginator shape
      if (data?.data && (data?.meta || data?.total !== undefined)) {
        items.value = data.data;
        pagination.value = {
          total: data.meta?.total ?? data.total ?? data.data.length,
          per_page: data.meta?.per_page ?? data.per_page ?? params.per_page ?? 25,
          current_page: data.meta?.current_page ?? data.current_page ?? 1,
        };
      } else {
        // array fallback
        items.value = Array.isArray(data) ? data : [];
        pagination.value = {
          total: items.value.length,
          per_page: params.per_page ?? 25,
          current_page: 1,
        };
      }
    } catch (e) {
      error.value = e?.response?.data?.message || "Failed to fetch training";
      toast(error.value, "error");
    } finally {
      loading.value = false;
    }
  }

  async function create(payload) {
    loading.value = true;
    try {
      await trainingService.create(payload);
      toast("Training created");
      await fetchAll({ page: 1 });
    } catch (e) {
      const msg = e?.response?.data?.message || "Failed to create";
      toast(msg, "error");
      throw e;
    } finally {
      loading.value = false;
    }
  }

  async function update(id, payload) {
    loading.value = true;
    try {
      await trainingService.update(id, payload);
      toast("Training updated");
      await fetchAll();
    } catch (e) {
      const msg = e?.response?.data?.message || "Failed to update";
      toast(msg, "error");
      throw e;
    } finally {
      loading.value = false;
    }
  }

  async function remove(id) {
    loading.value = true;
    try {
      await trainingService.delete(id);
      toast("Training deleted");
      await fetchAll();
    } catch (e) {
      const msg = e?.response?.data?.message || "Failed to delete";
      toast(msg, "error");
      throw e;
    } finally {
      loading.value = false;
    }
  }

  async function bulkDelete(ids) {
    loading.value = true;
    try {
      await trainingService.bulkDelete(ids);
      toast("Selected trainings deleted");
      await fetchAll();
    } catch (e) {
      const msg = e?.response?.data?.message || "Failed to bulk delete";
      toast(msg, "error");
      throw e;
    } finally {
      loading.value = false;
    }
  }

  return {
    items, pagination, loading, error, filters,
    fetchAll, create, update, remove, bulkDelete
  };
});
